import React from 'react';
import { useFetch } from '../../hooks/useFetch';
import DirectorPanelState from './DirectorPanelState';
import { formatTimestamp } from '../../utils/formatDate';

const SMS_TIMESTAMP_FALLBACK = 'No inbound texts received yet';

const PROVIDER_LABELS = {
  ez_texting: 'EZ Texting',
  twilio: 'Twilio'
};

/**
 * Client Portal — Integrations status (read-only).
 *
 * VizionOps is a managed-service SaaS. Integration *configuration*
 * (API credentials, auth tokens, webhook secrets, provider selection,
 * intake behavior) is administered by Platform Admin per company. The
 * Client Portal CONSUMES the integration and views status only — it
 * never exposes API keys, secrets, webhook setup, or credential forms.
 *
 * Reads `/api/integrations/sms-status`, a secret-free endpoint that
 * returns connection + webhook-health for both SMS providers and the
 * company's selected provider. No credential ever reaches the client.
 * There is no save action here; configuration changes go through
 * Platform Admin → CompanyManagementModal → Integrations.
 */
function DirectorIntegrationsPanel() {
  const { data: status, isLoading, error } = useFetch('/api/integrations/sms-status');

  const selectedProvider = status?.selectedProvider || 'ez_texting';
  const active = selectedProvider === 'twilio' ? status?.twilio : status?.ezTexting;
  const providerLabel = PROVIDER_LABELS[selectedProvider] || 'SMS';

  const connected = Boolean(active?.connected);
  const lastStatus = active?.webhookLastStatus || 'not_configured';
  const lastReceivedAt = active?.webhookLastReceivedAt || null;
  const lastError = active?.webhookLastError || '';

  return (
    <div className="director-admin-stack" data-testid="director-integrations-readonly">
      <DirectorPanelState kind="loading" message={isLoading ? 'Loading integration status...' : ''} />
      <DirectorPanelState kind="error" message={error} />

      <div className="director-split-grid">
        <section className="director-card">
          <div className="role-section-header">
            <h2>{providerLabel} SMS Intake</h2>
            <span data-testid="director-integrations-provider">{connected ? 'Connected' : 'Not connected'}</span>
          </div>
          <p className="role-section-copy">
            SMS-based attendance intake. Your active SMS provider,
            credentials, and intake behavior are managed by your VizionOps
            administrator. This view shows the live connection status for
            your location.
          </p>

          <div className="director-history-list">
            <div className="director-history-item">
              <div className="director-history-row">
                <strong>Connection</strong>
                <span data-testid="director-integrations-connection">
                  {connected ? 'Connected' : 'Not connected'}
                </span>
              </div>
              <div className="director-muted">
                {connected
                  ? `${providerLabel} credentials are configured for your company.`
                  : `No ${providerLabel} credentials are configured yet. Contact your VizionOps administrator to set this up.`}
              </div>
            </div>
            <div className="director-history-item">
              <div className="director-history-row">
                <strong>Webhook Status</strong>
                <span data-testid="director-integrations-webhook-status">{lastStatus}</span>
              </div>
              <div className="director-muted">
                Last inbound: {formatTimestamp(lastReceivedAt, SMS_TIMESTAMP_FALLBACK)}
              </div>
            </div>
            {lastError ? (
              <div className="director-history-item">
                <div className="director-history-row">
                  <strong>Last Error</strong>
                </div>
                <div className="director-warning-copy" data-testid="director-integrations-last-error">
                  {lastError}
                </div>
              </div>
            ) : null}
          </div>
        </section>

        <section className="director-card director-note-card">
          <div className="role-section-header">
            <h2>How Callouts Flow</h2>
            <span>Operational View</span>
          </div>
          <div className="director-history-list">
            <div className="director-history-item">
              <div className="director-muted">
                Inbound texts to your attendance number create attendance
                events for sick, absent, and late. Review them in the
                Requests &amp; Calendar → Callout History view.
              </div>
            </div>
            <div className="director-history-item">
              <div className="director-muted">
                To change the SMS provider, connected number, credentials,
                or intake behavior, contact your VizionOps administrator.
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default DirectorIntegrationsPanel;
